import Box from 'UIKit/Box/Box'
import Flex from 'UIKit/Box/Flex'
import { BoxProps } from 'UIKit/Box/types'
import Text from 'UIKit/Text'
import TokenLogo from 'UIKit/TokenLogo'
import useDebounce from 'hooks/useDebounce'
import useKeydownEventListener from 'hooks/useKeydownEventListener'
import useMatchBreakpoints from 'hooks/useMatchBreakpoints'
import Column from 'layout/Components/Column'
import { RowCenter } from 'layout/Components/Row'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Trans } from 'react-i18next'
import styled from 'styled-components'
import { range } from 'utils'
import { delineate } from 'utils/formatBalance'
import { InHouseGameButton, InHouseLabel, MineAnimation } from 'views/InhouseGame/components/styled'
import { MINE_GAME_SIZE } from '../../config'
import { generateMineGameMultiplier } from '../../config/utils'
import { useCurrentMineBet, useMineAction, useMineBlockOpenedListener, useOpenMineBlock } from '../../hooks'
import MineEstimateProfit from './MineEstimateProfit'

const MinePlay: React.FC<
  BoxProps & {
    mines: number
    hotkeyEnable: boolean
  }
> = ({ hotkeyEnable, ...props }) => {
  const currentBet = useCurrentMineBet()
  const [isLoading, setIsLoading] = useState(false)
  const submitBlock = useOpenMineBlock()
  const { finishBet } = useMineAction()
  const { isDesktop } = useMatchBreakpoints()

  const diamonds = useMemo(() => {
    if (!currentBet) return 0
    return currentBet.result.filter((item) => !item.isUserOpened).length - currentBet.mines
  }, [currentBet])

  const diamondsDebounced = useDebounce(diamonds, 500)

  const loadingBlocksRef = useRef(0)
  const openedBlockRef = useRef([])

  const profits = useMemo(() => {
    return generateMineGameMultiplier(currentBet.mines)
  }, [currentBet.mines])

  const currentProfitIndex = useMemo(() => {
    const index = MINE_GAME_SIZE * MINE_GAME_SIZE - diamonds - currentBet.mines - 1
    return index
  }, [profits, diamonds])

  useMineBlockOpenedListener((blockId) => {
    setIsLoading(true)
    loadingBlocksRef.current++
    openedBlockRef.current.push(blockId)
  })

  useEffect(() => {
    openedBlockRef.current = [
      ...currentBet.result.map((item, index) => (item.isUserOpened ? index : 0)).filter((item) => item),
    ]
  }, [])

  useEffect(() => {
    if (loadingBlocksRef.current <= 0) return
    loadingBlocksRef.current--
    if (loadingBlocksRef.current === 0) {
      setIsLoading(false)
    }
  }, [diamonds])

  const openRandom = () => {
    const unknowBlockIds = range(0, MINE_GAME_SIZE * MINE_GAME_SIZE)
      .map((item) => !openedBlockRef.current.includes(item) && item)
      .filter((item) => item)
    const randomIndex = Math.floor(Math.random() * unknowBlockIds.length - 1) + 1
    submitBlock(unknowBlockIds[randomIndex])
  }

  const cashout = () => {
    if (currentProfitIndex >= 0) {
      setIsLoading(true)
      finishBet()
    }
  }

  const eventHandler = useCallback(
    (event) => {
      if (event && (event.key === 'q' || event.key === 'w')) {
        event.preventDefault()

        if (!hotkeyEnable || isLoading) return null
        if (event.key === 'w') cashout()
        else openRandom()
      }
    },
    [hotkeyEnable, isLoading],
  )

  useKeydownEventListener(eventHandler)

  return (
    <StyledWrapper {...props}>
      {!isDesktop && (
        <Box width="100%" mb="14px">
          <StyledSecondaryButton width="100%" mb="14px" disabled={isLoading} onClick={openRandom}>
            <Trans>Pick random tile</Trans>
          </StyledSecondaryButton>

          <StyledButton width="100%" disabled={isLoading || currentProfitIndex < 0} onClick={cashout}>
            {isLoading && <MineAnimation mr="8px" />}
            <Trans>Cashout</Trans>
          </StyledButton>
        </Box>
      )}

      <Column mb="14px">
        <Flex justifyContent="space-between">
          <Box width="calc(50% - 6px)">
            <InHouseLabel>
              <Trans>Mines</Trans>
            </InHouseLabel>
            <StyledValueBox>
              <Text small>{currentBet.mines}</Text>
            </StyledValueBox>
          </Box>

          <Box width="calc(50% - 6px)">
            <InHouseLabel>
              <Trans>Diamonds</Trans>
            </InHouseLabel>
            <StyledValueBox>
              <Text small>{diamondsDebounced}</Text>
            </StyledValueBox>
          </Box>
        </Flex>
      </Column>
      <Box width="100%" mb="14px">
        <InHouseLabel>
          <Trans>
            Total Profit (
            {{
              multiplier: `${
                currentProfitIndex >= 0 ? delineate(profits[currentProfitIndex].toString(), 3) : '1.000'
              }x`,
            }}
            )
          </Trans>
        </InHouseLabel>
        <StyledValueBox>
          <Text small mr="12px">
            $
            {delineate(
              currentBet.betAmount.amount
                .multipliedBy(currentProfitIndex >= 0 ? profits[currentProfitIndex] : 1)
                .minus(currentBet.betAmount.amount)
                .toString(10),
              2,
            )}
          </Text>
          <TokenLogo
            token={currentBet.betAmount.token}
            size={16}
            minWidth={16}
            networkImageProps={{
              width: 10,
              height: 10,
            }}
          />
        </StyledValueBox>
      </Box>

      <Box width="100%" mb="18px">
        <InHouseLabel>
          <Trans>
            Profit on Next Tile (
            {{ multiplier: `${delineate(profits[currentProfitIndex + 1]?.toString() || '0', 3)}x` }})
          </Trans>
        </InHouseLabel>
        <StyledValueBox>
          <Text small mr="12px">
            $
            {profits[currentProfitIndex + 1]
              ? delineate(
                  currentBet.betAmount.amount
                    .multipliedBy(profits[currentProfitIndex + 1])
                    .minus(currentBet.betAmount.amount)
                    .toString(10),
                  2,
                )
              : '0'}
          </Text>
          <TokenLogo
            token={currentBet.betAmount.token}
            size={16}
            minWidth={16}
            networkImageProps={{
              width: 10,
              height: 10,
            }}
          />
        </StyledValueBox>
      </Box>

      <MineEstimateProfit
        width="100%"
        mb="18px"
        mines={currentBet?.mines}
        betAmount={currentBet?.betAmount}
        current={currentProfitIndex}
      />

      {isDesktop && (
        <Box width="100%">
          <StyledSecondaryButton width="100%" mb="14px" disabled={isLoading} onClick={openRandom}>
            <Trans>Pick random tile</Trans>
          </StyledSecondaryButton>

          <StyledButton width="100%" disabled={isLoading || currentProfitIndex < 0} onClick={cashout}>
            {isLoading && <MineAnimation mr="8px" />}
            <Trans>Cashout</Trans>
          </StyledButton>
        </Box>
      )}
    </StyledWrapper>
  )
}

const StyledWrapper = styled(Column)``

const StyledButton = styled(InHouseGameButton)`
  min-height: 42px !important;
  max-height: 42px !important;
`

const StyledSecondaryButton = styled(StyledButton)`
  background: ${({ theme }) => theme.colors.backgroundToggle}90 !important;
`

const StyledValueBox = styled(RowCenter)`
  padding-right: 12px;
  background: ${({ theme }) => theme.colors.backgroundAlt5};
  border-radius: ${({ theme }) => theme.radii.tiny};
  height: 40px;
  justify-content: flex-end;
  padding-left: 12px;
`

export default MinePlay
